import React, { useState, useEffect } from 'react';
import { UserPlus } from 'lucide-react';
import { t } from '@/i18n/ro';
import {
  getProjectRoles,
  getAvailableUsersForProject,
  addProjectMember,
  type ProjectRole,
} from '@/services/projects.service';
import {
  Button,
  Modal,
  SearchableSelect,
} from '@/components/common';
import type { User } from '@/types';

interface AddMemberModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: number;
  onMemberAdded: () => void;
}

export function AddMemberModal({
  isOpen,
  onClose,
  projectId,
  onMemberAdded,
}: AddMemberModalProps) {
  const [users, setUsers] = useState<User[]>([]);
  const [roles, setRoles] = useState<ProjectRole[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [selectedRoleIds, setSelectedRoleIds] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Încarcă utilizatorii și rolurile când modal-ul se deschide
  useEffect(() => {
    if (isOpen) {
      loadData();
    }
  }, [isOpen, projectId]);

  const loadData = async () => {
    setIsLoadingData(true);
    setError(null);

    try {
      const [usersData, rolesData] = await Promise.all([
        getAvailableUsersForProject(projectId),
        getProjectRoles(),
      ]);

      setUsers(usersData);
      // Filtrăm doar rolurile "Admin proiect" și "Membru"
      const filteredRoles = rolesData.filter(role =>
        role.name === 'Admin proiect' || role.name === 'Membru'
      );
      setRoles(filteredRoles);
    } catch (err) {
      console.error('Failed to load data:', err);
      setError('Nu s-au putut încărca datele.');
    } finally {
      setIsLoadingData(false);
    }
  };

  const handleRoleToggle = (roleId: number) => {
    setSelectedRoleIds((prev) =>
      prev.includes(roleId)
        ? prev.filter((id) => id !== roleId)
        : [...prev, roleId]
    );
  };

  const handleSubmit = async () => {
    if (!selectedUserId) {
      setError('Selectați un utilizator.');
      return;
    }

    if (selectedRoleIds.length === 0) {
      setError('Selectați cel puțin un rol.');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await addProjectMember(projectId, Number(selectedUserId), selectedRoleIds);

      // Reset form
      setSelectedUserId('');
      setSelectedRoleIds([]);

      // Notify parent and close
      onMemberAdded();
      onClose();
    } catch (err: unknown) {
      console.error('Failed to add member:', err);
      const errorMessage = err && typeof err === 'object' && 'message' in err
        ? (err as { message: string }).message
        : 'Nu s-a putut adăuga membrul.';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedUserId('');
    setSelectedRoleIds([]);
    setError(null);
    onClose();
  };

  const userOptions = users.map((u) => ({
    value: u.id,
    label: (u as unknown as { name?: string }).name || `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.login,
  }));

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Adaugă membru"
      size="md"
      footer={
        <div className="flex justify-end gap-3">
          <Button variant="secondary" onClick={handleClose}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            isLoading={isLoading}
            disabled={isLoadingData || !selectedUserId || selectedRoleIds.length === 0}
            leftIcon={<UserPlus className="w-4 h-4" />}
          >
            Adaugă
          </Button>
        </div>
      }
    >
      {error && (
        <div className="mb-4 p-3 bg-danger-50 border border-danger-200 rounded-lg text-danger-700 text-sm">
          {error}
        </div>
      )}

      {isLoadingData ? (
        <div className="py-8 text-center text-gray-500">
          Se încarcă...
        </div>
      ) : (
        <div className="space-y-6">
          {/* Selectare utilizator */}
          <div>
            <SearchableSelect
              label="Utilizator"
              required
              options={userOptions}
              value={selectedUserId}
              onChange={(val) => setSelectedUserId(String(val))}
              placeholder="Caută și selectează un utilizator..."
            />
            {users.length === 0 && (
              <p className="mt-2 text-sm text-gray-500">
                Nu există utilizatori disponibili pentru a fi adăugați.
              </p>
            )}
          </div>

          {/* Selectare roluri */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Roluri <span className="text-danger-500">*</span>
            </label>
            <div className="space-y-2">
              {roles.map((role) => (
                <label
                  key={role.id}
                  className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
                >
                  <input
                    type="checkbox"
                    checked={selectedRoleIds.includes(role.id)}
                    onChange={() => handleRoleToggle(role.id)}
                    className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    {role.name}
                  </span>
                </label>
              ))}
            </div>
            {roles.length === 0 && (
              <p className="mt-2 text-sm text-gray-500">
                Nu există roluri disponibile.
              </p>
            )}
          </div>

          {/* Info */}
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-700">
              <strong>Notă:</strong> Membrul va primi acces la proiect conform rolurilor selectate.
            </p>
          </div>
        </div>
      )}
    </Modal>
  );
}
